/**
 * PWA update / stale-cache helpers.
 *
 * Pure, injectable logic so it can be unit-tested without a browser:
 * - fetchBuildId: defensive release check against /version.json (never throws,
 *   never causes reload loops on transient errors).
 * - shouldDeferUpdate: protects active forms, wizard drafts, cart and
 *   checkout/payment state from a forced reload.
 * - isAppCacheName: scoped cache lifecycle — only ANAYA's own Workbox/runtime
 *   caches, never other origin data (auth storage, Firebase, etc.).
 * - makeThrottled: single-flight throttling for startup/focus/visibility/online
 *   checks.
 * - hasReloadedFor / markReloadedFor: per-target durable guard (no loops,
 *   no A/B/A ping-pong).
 * - isServiceWorkerAllowed / isCacheableImageRequest: registration and
 *   image-caching policies.
 */

export const VERSION_URL = "/version.json";
export const RELOAD_GUARD_KEY = "anaya-pwa-reload-guard";
export const MIGRATION_KEY = "anaya-pwa-migrated-v2";
export const UPDATE_CHANNEL = "anaya-pwa-update";
export const CHECK_INTERVAL_MS = 60_000;

/** Fetch the build id of the currently deployed release. Returns null on any
 *  problem (offline, non-JSON, wrong shape) so callers simply skip the check. */
export async function fetchBuildId(
  fetchImpl: typeof fetch,
  url: string = VERSION_URL,
): Promise<string | null> {
  try {
    // Cast: this file is also type-checked under the Node config (vite.config
    // imports it), whose RequestInit lacks `cache`.
    const res = await fetchImpl(`${url}?t=${Date.now()}`, { cache: "no-store" } as RequestInit);
    if (!res.ok) return null;
    const data: unknown = await res.json();
    if (
      data &&
      typeof data === "object" &&
      typeof (data as { buildId?: unknown }).buildId === "string" &&
      (data as { buildId: string }).buildId.length > 0
    ) {
      return (data as { buildId: string }).buildId;
    }
    return null;
  } catch {
    return null;
  }
}

/** Paths where a forced reload would interrupt payment, a multi-step form or
 *  an in-progress wizard (even one without a persisted draft yet). */
const DEFER_PATH_PREFIXES = [
  "/checkout",
  "/pay/",
  "/payment-success",
  "/payment-canceled",
  "/salesman/order",
  "/salesman/perfume-wizard",
  "/salesman/corporate-gifting-wizard",
  "/personalization",
  "/selection",
  "/quiz",
  "/results",
  "/shipping-address",
  "/login-details",
  "/auth",
];

/** sessionStorage keys whose presence means an in-progress journey exists. */
const DRAFT_KEYS = [
  "corporateWizardState",
  "quizData",
  "quizFormData",
  "quizCurrentStep",
  "checkoutFormData",
  "checkoutSelectedItems",
  "currentOrderId",
  "perfumes",
  "editingCartItem",
];

/** True when reloading now could lose the user's in-progress work or payment. */
export function shouldDeferUpdate(
  pathname: string,
  getItem: (key: string) => string | null,
): boolean {
  if (DEFER_PATH_PREFIXES.some((p) => pathname.startsWith(p))) return true;
  return DRAFT_KEYS.some((key) => {
    try {
      return getItem(key) != null;
    } catch {
      return false;
    }
  });
}

/** True only for caches created by ANAYA's own service worker (Workbox
 *  precache/runtime plus our named runtime caches). Everything else — auth
 *  storage, other libraries' caches — is left untouched. */
export function isAppCacheName(name: string): boolean {
  return (
    /workbox-precache/.test(name) ||
    /workbox-runtime/.test(name) ||
    name === "google-fonts-cache" ||
    name === "gstatic-fonts-cache" ||
    name === "images-cache" ||
    name === "anaya-pages-cache"
  );
}

/** Single-flight throttle: repeated triggers inside the interval are ignored,
 *  and concurrent calls share one in-flight invocation. */
export function makeThrottled<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
  minIntervalMs: number,
  now: () => number = () => Date.now(),
): (...args: A) => Promise<R | undefined> {
  let last = -Infinity;
  let inflight: Promise<R> | null = null;
  return (...args: A) => {
    if (inflight) return inflight;
    const t = now();
    if (t - last < minIntervalMs) return Promise.resolve(undefined);
    last = t;
    inflight = Promise.resolve(fn(...args)).finally(() => {
      inflight = null;
    });
    return inflight;
  };
}

/**
 * Durable per-target reload guard (sessionStorage survives the reload).
 * Stores EVERY target build this tab has already reloaded into, so an A→B→A
 * flip-flop between deployments can never cause a ping-pong reload loop.
 */
const MAX_GUARD_ENTRIES = 20;

function readGuard(storage: Pick<Storage, "getItem">): string[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(RELOAD_GUARD_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

export function hasReloadedFor(storage: Pick<Storage, "getItem">, target: string): boolean {
  return readGuard(storage).includes(target);
}

/** Records the target. Returns false if it was already recorded or storage
 *  is unavailable — callers must then NOT reload (fail safe, never loop). */
export function markReloadedFor(
  storage: Pick<Storage, "getItem" | "setItem">,
  target: string,
): boolean {
  try {
    const list = readGuard(storage);
    if (list.includes(target)) return false;
    list.push(target);
    storage.setItem(RELOAD_GUARD_KEY, JSON.stringify(list.slice(-MAX_GUARD_ENTRIES)));
    return hasReloadedFor(storage, target);
  } catch {
    return false;
  }
}

/** Exported for the legacy-migration worker sync test. */
export const PROTECTED_PATH_PREFIXES: readonly string[] = DEFER_PATH_PREFIXES;

/** Service workers never register in dev, iframes, Lovable preview hosts or
 *  with ?sw=off; in those contexts existing /sw.js registrations are removed. */
export function isServiceWorkerAllowed(ctx: {
  prod: boolean;
  inIframe: boolean;
  hostname: string;
  search: string;
}): boolean {
  if (!ctx.prod || ctx.inIframe) return false;
  const h = ctx.hostname;
  if (h.startsWith("id-preview--") || h.startsWith("preview--")) return false;
  if (/(^|\.)lovableproject\.com$/.test(h)) return false;
  if (/(^|\.)lovableproject-dev\.com$/.test(h)) return false;
  if (/(^|\.)beta\.lovable\.dev$/.test(h)) return false;
  if (new URLSearchParams(ctx.search).get("sw") === "off") return false;
  return true;
}

/**
 * Image runtime-cache predicate (serialized into the generated service worker,
 * so it must stay self-contained: no outer references).
 * Caches only same-origin images and public storage objects. Signed,
 * authenticated or token-bearing URLs and requests with an Authorization
 * header are never cached — private media stays out of Cache Storage.
 */
export function isCacheableImageRequest({
  url,
  request,
  sameOrigin,
}: {
  url: URL;
  request: Request;
  sameOrigin: boolean;
}): boolean {
  if (request.method !== "GET") return false;
  const path = url.pathname;
  const isImage =
    request.destination === "image" || /\.(?:png|jpe?g|svg|gif|webp|avif)$/i.test(path);
  if (!isImage) return false;
  if (request.headers.has("authorization")) return false;
  if (/\/storage\/v1\/(?:object|render\/image)\/(?:sign|authenticated)\//.test(path)) return false;
  if (/[?&](?:token|x-amz-signature|signature|sig|expires)=/i.test(url.search)) return false;
  if (sameOrigin) return true;
  return /\/storage\/v1\/(?:object|render\/image)\/public\//.test(path);
}
